/**
 * Shared drag interaction helpers for coins and objects
 * Ensures consistent drag behavior across different entity types
 */

/**
 * Creates a drag handler system for a PIXI container
 * @param {Object} config - Configuration object
 * @param {PIXI.Container} config.container - The container to make draggable
 * @param {PIXI.Container} config.viewport - The viewport for global event listeners
 * @param {Function} config.onDragEnd - Callback when drag ends with movement
 * @param {Function} config.onSelect - Callback when clicked without dragging
 * @param {string} config.entityId - Unique ID of the entity
 * @param {string} config.selectedId - Currently selected entity ID
 * @param {boolean} config.canDrag - Whether dragging is allowed
 * @param {Object} config.visualEffects - Optional visual effects during drag
 * @returns {Function} Cleanup function to remove event listeners
 */
export function createDragHandler({
  container,
  viewport,
  onDragEnd,
  onSelect,
  entityId,
  selectedId,
  canDrag = true,
  visualEffects = {}
}) {
  const {
    onDragStart = null,
    onDragMove = null,
    dragCursor = 'grabbing',
    normalCursor = 'grab',
    dragAlpha = 0.7,
    normalAlpha = 1,
  } = visualEffects;

  let dragData = null;
  let dragOffset = { x: 0, y: 0 };
  let hasMoved = false;
  const startPos = { x: 0, y: 0 };

  // Global move handler for smooth dragging
  const onGlobalMove = (event) => {
    if (dragData && canDrag) {
      hasMoved = true;
      const newPosition = event.getLocalPosition(container.parent);
      container.x = newPosition.x + dragOffset.x;
      container.y = newPosition.y + dragOffset.y;
      
      // Optional custom drag move callback
      if (onDragMove) {
        onDragMove(container, event);
      }
    }
  };

  // Global up handler to end drag anywhere
  const onGlobalUp = () => {
    if (!dragData) return;
    
    // Remove global listeners
    viewport.off('pointermove', onGlobalMove);
    viewport.off('pointerup', onGlobalUp);
    viewport.off('pointerupoutside', onGlobalUp);
    
    // Resume viewport dragging (was paused during entity drag)
    if (viewport.plugins?.get('drag')) {
      viewport.plugins.get('drag').resume();
    }
    
    // Check if this was a click (no significant movement) vs a drag
    const didMove = hasMoved && (
      Math.abs(container.x - startPos.x) > 5 ||
      Math.abs(container.y - startPos.y) > 5
    );
    
    if (!didMove && onSelect) {
      // This was a click - toggle selection
      if (selectedId === entityId) {
        onSelect(null); // Deselect
      } else {
        onSelect(entityId); // Select
      }
    }
    
    if (canDrag) {
      container.cursor = normalCursor;
      container.alpha = normalAlpha;
      
      if (didMove && onDragEnd) {
        // Call the drag end callback with the container and start position
        onDragEnd(container, startPos);
      } else if (!didMove) {
        // Reset position if no movement (was just a click)
        container.x = startPos.x;
        container.y = startPos.y;
      }
    }
    
    dragData = null;
    hasMoved = false;
  };

  // Pointer down handler
  const onPointerDown = (event) => {
    // Only handle left-click or touch
    if (event.button !== undefined && event.button !== 0) return;
    
    // Stop event propagation to prevent viewport from handling it
    event.stopPropagation();
    
    hasMoved = false;
    startPos.x = container.x;
    startPos.y = container.y;
    
    if (canDrag) {
      dragData = event;
      const position = event.getLocalPosition(container.parent);
      dragOffset.x = container.x - position.x;
      dragOffset.y = container.y - position.y;
      container.cursor = dragCursor;
      container.alpha = dragAlpha;
      
      // Optional custom drag start callback
      if (onDragStart) {
        onDragStart(container, event);
      }
      
      // Pause viewport dragging while dragging entity (important for touch devices)
      if (viewport.plugins?.get('drag')) {
        viewport.plugins.get('drag').pause();
      }
      
      // Register global move/up handlers on viewport for smooth dragging
      viewport.on('pointermove', onGlobalMove);
      viewport.on('pointerup', onGlobalUp);
      viewport.on('pointerupoutside', onGlobalUp);
    }
  };

  // Attach the pointerdown listener
  container.on('pointerdown', onPointerDown);

  // Return cleanup function
  return () => {
    container.off('pointerdown', onPointerDown);
    viewport.off('pointermove', onGlobalMove);
    viewport.off('pointerup', onGlobalUp);
    viewport.off('pointerupoutside', onGlobalUp);
  };
}

/**
 * Helper to check if movement exceeds threshold (distinguishes click from drag)
 * @param {number} startX - Starting X position
 * @param {number} startY - Starting Y position
 * @param {number} endX - Ending X position
 * @param {number} endY - Ending Y position
 * @param {number} threshold - Movement threshold in pixels (default: 5)
 * @returns {boolean} True if movement exceeds threshold
 */
export function didMove(startX, startY, endX, endY, threshold = 5) {
  return Math.abs(endX - startX) > threshold || Math.abs(endY - startY) > threshold;
}

/**
 * Snaps a position to grid
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} gridSize - Size of grid cells
 * @returns {Object} Snapped position {x, y}
 */
export function snapToGrid(x, y, gridSize) {
  return {
    x: Math.round(x / gridSize) * gridSize,
    y: Math.round(y / gridSize) * gridSize
  };
}

